import Select from "react-select";
import { useSelector } from "react-redux";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Panel, PanelHeader, PanelBody, PanelFooter } from "../../components/panel/panel.jsx";
import { useSaveTercero, useUpdateTercero, useDeleteTercero } from "../../hooks_cache/useTerceros.js";
import { useQueryClient } from "@tanstack/react-query";

import { useActividadesEconomicas } from "../../hooks_cache/useActividadesEconomicas";
import { useDepartamentos } from "../../hooks_cache/useDepartamento.js";
import { useMunicipios } from "../../hooks_cache/useMunicipio.js";
import { useTipoDocumento } from "../../hooks_cache/useTipoDocumento.js";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const initialFormData = {
  nombre: "",
  nrc: "",
  nit: "",
  direccion_completa: "",
  categoria: "PEQUENO",
  diascredito: 0,
  id_actividadEconomica: null,
  correo: "",
  correo_en_copia: null,
  telefono: "",
  es_cliente: false,
  es_proveedor: false,
  agente_retencion: false,
  // tipoDocumento: "13",
  numDocumento: null,
  id_tipoDocumento: null,
};

const TerceroInfo = () => {
  const navigate = useNavigate();
  const { empresaSeleccionada } = useSelector((state) => state.auth);
  const id_empresa = empresaSeleccionada?.id; // ✅ Obtener `id_empresa` del estado global
  const { id } = useParams();
  const queryClient = useQueryClient();
  const cachedTerceros = queryClient.getQueryData(["terceros", id_empresa]);
  const tercero = cachedTerceros?.find((t) => t.id === parseInt(id));

  const [formData, setFormData] = useState(initialFormData);

  const { data: actividades } = useActividadesEconomicas();
  const { data: municipios } = useMunicipios();
  const { data: departamentos } = useDepartamentos();
  const { data: tipoDocumento } = useTipoDocumento();

  const { mutate: saveTercero, isLoading: isSaving } = useSaveTercero();
  const { mutate: updateTercero, isLoading: isUpdating } = useUpdateTercero();
  const { mutate: deleteTercero, isLoading: isDeleting } = useDeleteTercero();

  const [errores, setErrores] = useState({});

  useEffect(() => {
    if (tercero) {
      let correo_en_copia = [];
      if (tercero.correo_en_copia) {
        correo_en_copia = JSON.parse(tercero.correo_en_copia);
      }

      setFormData((prev) => ({ ...prev, ...tercero, correo_en_copia }));
    }
  }, [tercero]);

  // Manejar cambios en los inputs
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Manejar cambios en select
  const handleSelectChange = (selectedOption, { name }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : null,
    }));
  };

  // Manejar envío del formulario
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      // alert("Debe completar todos los campos obligatorios.");
      return;
    }

    let correo_en_copia = "";
    if (formData.correo_en_copia) {
      correo_en_copia = formData.correo_en_copia;
    }

    if (id) {
      updateTercero({ id, ...formData, correo_en_copia }, { onSuccess: () => navigate("/terceros") });
    } else {
      saveTercero({ ...formData, correo_en_copia }, { onSuccess: () => navigate("/terceros") });
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "¿Está seguro?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        console.log("✅ Confirmado, intentando borrar...");
        deleteTercero([id], {
          onSuccess: ({ deletedIds, failedIds }) => {
            navigate(-1);
          },
          onError: (error) => {
            toast.error(error.mensaje + " - " + error.error);
          },
        });
        console.log("✅ Llamado deleteTercero terminado");
      }
    });
  };

  const duiRegex = /^\d{8}-\d{1}$/;
  const nitRegex = /^\d{14}$/;
  const validate = () => {
    let nuevosErrores = {};

    if (!formData.nombre) nuevosErrores["nombre"] = "El campo nombre es obligatorio.";
    if (!formData.direccion_completa) nuevosErrores["direccion_completa"] = "El campo dirección es obligatorio.";
    if (!formData.correo) nuevosErrores["correo"] = "El campo correo es obligatorio.";
    if (!formData.telefono) nuevosErrores["telefono"] = "El campo teléfono es obligatorio.";
    if (!formData.id_departamento) nuevosErrores["id_departamento"] = "El campo departamento es obligatorio.";
    if (!formData.id_municipio) nuevosErrores["id_municipio"] = "El campo municipio es obligatorio.";
    if (!formData.es_cliente && !formData.es_proveedor)
      nuevosErrores["tipo_tercero"] = "Debe seleccionar al menos una opción, cliente o proveedor.";

    if (formData.nrc.length > 0 && formData.id_actividadEconomica == null) {
      nuevosErrores["actividad_economica"] = "Si asigna un numero registro fiscal (NRC) debe especificar su actividad economica.";
    }

    formData.numDocumento = formData.numDocumento?.trim() || "";
    if (selectedTipoDocumento?.codigo === "13" && !(formData.numDocumento === "" || duiRegex.test(formData.numDocumento))) {
      nuevosErrores["numDocumento"] = "El campo DUI es inválido, debe tener el formato 00000000-0.";
    } else if (selectedTipoDocumento?.codigo === "36" && !(formData.numDocumento === "" || nitRegex.test(formData.numDocumento))) {
      nuevosErrores["numDocumento"] = "El campo NIT es inválido, debe ingresarlo sin guiones y tener 14 dígitos.";
    }

    if (Object.keys(nuevosErrores).length > 0) {
      setErrores(nuevosErrores);
      return false;
    }

    setErrores({});
    return true;
  };

  // Reutilizar renderizado de inputs
  const renderInput = (label, name, col = 3, type = "text") => (
    <div className={`col-md-${col}`}>
      <div className="form-floating mb-3 mb-md-0">
        <input
          type={type}
          className="form-control fs-15px"
          placeholder={label}
          name={name}
          value={formData[name] || ""}
          onChange={handleChange}
        />
        <label className="d-flex align-items-center fs-13px">{label}</label>
      </div>
      {errores[name] && <p style={{ color: "red" }}>* {errores[name]}</p>}
    </div>
  );

  // Reutilizar renderizado de select
  const renderSelect = (label, name, options) => (
    <div className="col-md-3">
      <div className="form-floating mb-3">
        <select className="form-select" name={name} value={formData[name] || ""} onChange={handleChange}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <label className="d-flex align-items-center fs-13px">{label}</label>
      </div>
      {errores[name] && <p style={{ color: "red" }}>* {errores[name]}</p>}
    </div>
  );

  const departamentoOptions =
    departamentos?.map((depto) => ({
      value: depto.id,
      label: `${depto.codigo} - ${depto.descripcion}`,
    })) || [];

  const selectedDepartamento = departamentoOptions.find((opt) => opt.value === formData.id_departamento) || null;

  const tipoDocumentoOptions =
    tipoDocumento?.map((td) => ({
      ...td,
      value: td.id,
      label: td.descripcion,
    })) || [];

  const selectedTipoDocumento = tipoDocumentoOptions.find((opt) => opt.value === formData.id_tipoDocumento) || null;

  const handleCorreosEnCopiaChange = (correosValidos) => {
    setFormData((prev) => ({
      ...prev,
      correo_en_copia: JSON.stringify(correosValidos),
    }));
  };

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item">
          <Link to="/terceros">Terceros</Link>
        </li>
        <li className="breadcrumb-item active">Dato de cliente</li>
      </ol>
      <h1 className="page-header">Datos de tercero</h1>

      <div className="row mb-3">
        <div className="col-xl-12">
          <Panel>
            <PanelHeader noButton={true}>Tercero:</PanelHeader>

            <PanelBody>
              <form onSubmit={handleSubmit}>
                <div className="row mb-md-4">
                  {renderInput("NOMBRE O RAZON SOCIAL", "nombre", 6)}
                  {renderInput("NUMERO DE REGISTRO", "nrc", 3)}
                  {renderInput("NIT", "nit", 3)}
                </div>

                <div className="row mb-md-4">
                  {renderInput("DIRECCION", "direccion_completa", 6)}

                  <div className="col-md-3">
                    <div className="mb-3 mb-md-0">
                      <label className="form-label">MUNICIPIO:</label>
                      <Select
                        name="id_municipio"
                        options={municipios?.map((municipio) => ({
                          value: municipio.id,
                          label: `${municipio.codigo} - ${municipio.descripcion}`,
                        }))}
                        value={
                          formData.id_municipio
                            ? {
                                value: formData.id_municipio,
                                label: municipios?.find((a) => a.id === formData.id_municipio)?.["descripcion"] || "Error..",
                              }
                            : null
                        }
                        isSearchable
                        placeholder="Seleccione un municipio"
                        onChange={handleSelectChange}
                      />
                      {errores["id_municipio"] && <p style={{ color: "red" }}>* {errores["id_municipio"]}</p>}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3 mb-md-0">
                      <label className="form-label">DEPARTAMENTO:</label>
                      <Select
                        name="id_departamento"
                        options={departamentoOptions}
                        value={selectedDepartamento}
                        isSearchable
                        placeholder="Seleccione un departamento"
                        onChange={handleSelectChange}
                      />
                      {errores["id_departamento"] && <p style={{ color: "red" }}>* {errores["id_departamento"]}</p>}
                    </div>
                  </div>
                </div>

                <div className="row mb-md-4">
                  {renderSelect("CATEGORIA CONTRIBUYENTE", "categoria", [
                    { value: "PEQUENO", label: "PEQUEÑO" },
                    { value: "MEDIANO", label: "MEDIANO" },
                    { value: "GRANDE", label: "GRANDE" },
                  ])}
                  {renderInput("DIAS DE CREDITO", "diascredito", 3, "number")}

                  <div className="col-md-6">
                    <div className="mb-3 mb-md-0">
                      <label className="form-label">ACTIVIDAD ECONOMICA:</label>
                      <Select
                        name="id_actividadEconomica"
                        options={actividades?.map((actividad) => ({
                          value: actividad.id,
                          label: `${actividad.codigo} - ${actividad.descripcion}`,
                        }))}
                        value={
                          formData.id_actividadEconomica
                            ? {
                                value: formData.id_actividadEconomica,
                                label: actividades?.find((a) => a.id === formData.id_actividadEconomica)?.["descripcion"] || "Error..",
                              }
                            : null
                        }
                        isSearchable
                        isClearable
                        placeholder="Seleccione una actividad economica"
                        onChange={handleSelectChange}
                      />
                      {errores["actividad_economica"] && <p style={{ color: "red" }}>* {errores["actividad_economica"]}</p>}
                    </div>
                  </div>
                </div>

                <div className="row mb-md-4">
                  {renderInput("EMAIL PRINCIPAL", "correo")}
                  {renderInput("TELEFONO", "telefono")}

                  <div className="col-md-3">
                    <div className="mb-3 mb-md-0">
                      <label className="form-label">TIPO DOCUMENTO:</label>
                      <Select
                        name="id_tipoDocumento"
                        options={tipoDocumentoOptions}
                        value={selectedTipoDocumento}
                        isSearchable
                        isClearable
                        placeholder="Seleccione un tipo de documento"
                        onChange={handleSelectChange}
                      />
                      {errores["tipo_documento"] && <p style={{ color: "red" }}>* {errores["tipo_documento"]}</p>}
                    </div>
                  </div>

                  {renderInput("Numero Documento", "numDocumento")}
                </div>

                <div className="row mb-md-4">
                  <InputCorreosEnCopia correoEnCopia={formData.correo_en_copia} onValidChange={handleCorreosEnCopiaChange} />
                </div>

                <div className="row mb-md-4">
                  <label className="form-label col-form-label col-md-2">REGISTRAR TERCERO COMO: </label>
                  <div className="col-md-4">
                    <div className="form-check mt-2 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="es_cliente"
                        checked={formData.es_cliente}
                        onChange={handleChange}
                      />
                      <label className="form-check-label">CLIENTE</label>
                    </div>
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="es_proveedor"
                        checked={formData.es_proveedor}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="flexCheckChecked">
                        PROVEEDOR
                      </label>
                    </div>

                    {errores["tipo_tercero"] && <p style={{ color: "red" }}>* {errores["tipo_tercero"]}</p>}
                  </div>

                  <label className="form-label col-form-label col-md-2">APLICAR RETENCION DE IVA: </label>
                  <div className="col-md-4">
                    <div className="form-check mt-2 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="agente_retencion"
                        checked={formData.agente_retencion}
                        onChange={handleChange}
                      />
                      <label className="form-check-label">
                        RETENCION 1% <small>(ventas sujetas mayores a $100)</small>
                      </label>
                    </div>
                  </div>
                </div>
              </form>
            </PanelBody>
            <PanelFooter className="text-center border-1">
              <button onClick={() => navigate(-1)} className="btn btn-default btn-lg ms-5px mb-2">
                Cancelar
              </button>

              <button disabled={isSaving} onClick={handleSubmit} className="btn btn-green btn-lg ms-5px mb-2">
                {isSaving || isUpdating ? "Guardando..." : "Guardar Cambios"}
              </button>

              <button disabled={isDeleting} onClick={() => handleDelete(tercero.id)} className="btn btn-red btn-lg ms-5px mb-2">
                {isDeleting ? "Eliminando..." : "Eliminar"}
              </button>
            </PanelFooter>
          </Panel>
        </div>
      </div>
    </div>
  );
};

const InputCorreosEnCopia = ({ correoEnCopia = [], onValidChange = () => {} }) => {
  const [valor, setValor] = useState("");
  const [errores, setErrores] = useState([]);

  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validarCorreos = (texto) => {
    const correos = texto
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c.length > 0);

    const correosInvalidos = correos.filter((c) => !regex.test(c));
    setErrores(correosInvalidos);

    // Solo devolver los válidos al padre
    const correosValidos = correos.filter((c) => regex.test(c));
    onValidChange(correosValidos);
  };

  const handleChange = (e) => {
    const texto = e.target.value;
    setValor(texto);
    validarCorreos(texto);
  };

  useEffect(() => {
    if (Array.isArray(correoEnCopia) && correoEnCopia.length > 0) {
      const texto = Object.values(correoEnCopia).join(", ");
      setValor(texto);
      validarCorreos(texto);
    }
  }, [correoEnCopia]);

  return (
    <div>
      <div className={`col-md-6 col-sm-12`}>
        <div className="form-floating mb-3 mb-md-0">
          <input
            type="text"
            value={valor}
            onChange={handleChange}
            placeholder="ejemplo1@gmail.com, ejemplo2@hotmail.com"
            className="form-control fs-12px"
          />
          <label className="d-flex align-items-center fs-13px">EMAIL EN COPIA (separados por comas):</label>
        </div>
        {errores.length > 0 ? (
          <p style={{ color: "red", marginTop: "6px" }}>Correos inválidos: {errores.join(", ")}</p>
        ) : valor.trim() ? (
          <p style={{ color: "green", marginTop: "6px" }}>Todos los correos son válidos ✅</p>
        ) : null}
      </div>
    </div>
  );
};

export default TerceroInfo;
